import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { formatMinutes, formatDecimalHours, formatWeekLabel } from "@/lib/timeFormat";
import { Loader2 } from "lucide-react";
import StatusPill from "@/components/hours/StatusPill";
import StatusFilter from "@/components/hours/StatusFilter";
import HoursSummaryStrip from "@/components/hours/HoursSummaryStrip";

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await api.get("/time/my-timesheets/history?weeks=12");
      setItems(data.items);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!status) return items;
    return items.filter((w) => (w.status || "draft") === status);
  }, [items, status]);

  // Phase 24 — 12-week window for the summary strip.
  const range = useMemo(() => {
    if (items.length === 0) return { from: undefined, to: undefined };
    const dates = items.map((w) => w.period_start_local).sort();
    return { from: dates[0], to: items[0]?.period_end_local };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">My time</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">History</h1>
        <p className="mt-2 text-muted-foreground">Past 12 pay weeks. Click a week to view its entries.</p>
      </div>

      {range.from && range.to && (
        <HoursSummaryStrip from={range.from} to={range.to} variant="worker" />
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground" data-testid="history-filter-caption">
          {status
            ? `Showing ${status} weeks (${filtered.length})`
            : `Showing all ${filtered.length} weeks`}
        </div>
        <StatusFilter
          value={status}
          onChange={setStatus}
          persistKey="hours-filter:worker-history"
        />
      </div>

      <Card>
        <CardHeader><CardTitle>Timesheets</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Week</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Entries</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    No weeks match this filter.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((w) => (
                <TableRow key={w.period_start_local} data-testid={`week-row-${w.period_start_local}`}>
                  <TableCell className="font-medium">{formatWeekLabel(w.period_start_local, w.period_end_local)}</TableCell>
                  <TableCell>{formatMinutes(w.total_minutes)} <span className="text-muted-foreground">({formatDecimalHours(w.total_minutes)}h)</span></TableCell>
                  <TableCell>{w.entries_count}</TableCell>
                  <TableCell>
                    <StatusPill status={w.status || "draft"} size="sm" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/app/time/week?period=${w.period_start_local}`}>View</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
